import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import type { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useContext, useMemo } from 'react'
import { OtherComponentTypes } from '../../../types/otherComponent'
import type { ComponentTypesV2 } from '../../../types/selectComponent'
import { HardwareSelectorFilterSidebarContext } from '../../hardwareSelectorV2/elements/HardwareSelectorFilterSidebarContext'
import listQueryParamsFormatter from '../../input/listQueryParamsFormatter'
import BasicFilter from '../basic/BasicFilter'
import { DISTRIBUTOR_FILTER_KEY_V2 } from '../distributor/constants'
import {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType, type OptionGenericType
} from '../typeV2'
import { parseStringifiedListFilterValue } from '../utils/utils'
import { DISTRIBUTOR_BRAND_FILTER_KEY_V2 } from './constants'
import useMultiDistributorsAvailableBrands from './useMultiDistributorsAvailableBrands'

export { DISTRIBUTOR_BRAND_FILTER_KEY_V2 } from './constants'
const defaultSelectedOptionValue = 'all'
export const SELECT_ALL_OPTION = { id: 'default', title: 'All', value: defaultSelectedOptionValue }


type ManufacturerFilterOptionsType = OptionGenericType<string>[]


const getManufacturerOptions = ({
                                  configs,
                                  orgIso2,
                                }: {
  configs
  orgIso2: string | undefined
}): ManufacturerFilterOptionsType | undefined => {
  if (!configs || configs.length === 0) {
    return undefined
  }

  const options: ManufacturerFilterOptionsType = configs.reduce((result, option) => {
    if (option.include_country && !option.include_country.includes(orgIso2)) {
      return result
    }

    result.push({ title: option.title, value: option.value, id: option.value })

    return result
  }, [])

  if (options.length === 0) {
    return undefined
  }

  return options
}

type DistributorBrandManufacturerFilterPropsType = {
  componentTypes?: ComponentTypesV2[]
}

const DistributorBrandManufacturerFilter = <T, >({
                                                   label = 'Brand',
                                                   distributors,
                                                   componentTypes,
                                                   otherComponentTypes,
                                                   rendererComponent: RendererComponent,
                                                   ...props
                                                 }: FilterGenericPropsType &
  DistributorBrandManufacturerFilterPropsType &
  ConfigurableFilterOptionsType<string, T> & {
  distributors: HardwareSupplierFilterKeyType[]
  otherComponentTypes: Set<OtherComponentTypes>
}) => {
  const defaultValue = new Set([SELECT_ALL_OPTION.value])
  let availableBrands = useMultiDistributorsAvailableBrands({
    distributors: distributors,
    componentType: componentTypes,
    otherComponentTypes: otherComponentTypes,
  })

  
  const options = useMemo(() => {
    return availableBrands.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).reduce(
      (options, brand) => {
        return options.concat({ id: brand, value: encodeURIComponent(brand), title: brand })
      },
      [SELECT_ALL_OPTION],
    )
  }, [availableBrands])

  const sideEffect = <OptionValue, RendererValue>({ filterValues }: { filterValues: FilterValuesType }) => {
    const newFilterValue = { ...filterValues }

    const brandNames = listQueryParamsFormatter.parse(filterValues[DISTRIBUTOR_BRAND_FILTER_KEY_V2])

    if (brandNames.has(SELECT_ALL_OPTION.value)) {
      const allBrands = new Set([...options.map((option) => option.value)])
      allBrands.delete(SELECT_ALL_OPTION.value)
      newFilterValue[DISTRIBUTOR_BRAND_FILTER_KEY_V2] = listQueryParamsFormatter.format({ value: allBrands })
    }

    return newFilterValue
  }

  return (
    <BasicFilter
      label={label}
      rendererComponent={RendererComponent}
      defaultSelectedOptionValue={defaultValue}
      filterKey={DISTRIBUTOR_BRAND_FILTER_KEY_V2}
      options={options}
      sideEffect={sideEffect}
      {...props}
    />
  )
}

const createDistributorBrandFilterNode = <T, >(
  config: ConfigurableFilterOptionsType<string, T>,
): FilterComponentNodeV2 => {
  const FilterComponent = (filterProps: FilterGenericPropsType) => {
    const { location } = useContext(HardwareSelectorFilterSidebarContext)
    const { filteredSuppliers: enabledSuppliers } = useEnabledHardwareSuppliers()
    const setDistributors = listQueryParamsFormatter.parse<HardwareSupplierFilterKeyType>(filterProps.allFilterValues[DISTRIBUTOR_FILTER_KEY_V2])
    const distributorList = Array.from(setDistributors) as HardwareSupplierFilterKeyType[]

    // The `types` on query parameters for componentsV2 endpoint is shared by 'other' sub-components
    // and Major component such as Panel and inverters, Battery.
    // Here, we can separate it using filterProps, which isn't available in the downstream components.
    const selectorConfigTypes = parseStringifiedListFilterValue<OtherComponentTypes>({
      filterValues: filterProps.persistentFilterValues, // Hardware selector sets what available at creation time
      key: 'types',
    })

    // In the Shop, the brand filter should always reflect the actual catalog rather
    // than the static `manufacturer_filter` public feature config. When no specific
    // supplier is selected (e.g. on initial load or with the "All" option), fall back
    // to the list of suppliers enabled for the org so brands are still fetched from
    // the dynamic `manufacturer_list` endpoint.
    let effectiveDistributors: HardwareSupplierFilterKeyType[] = distributorList
    if (effectiveDistributors.length === 0 && location === 'shop') {
      effectiveDistributors = enabledSuppliers as HardwareSupplierFilterKeyType[]
    }

    if (effectiveDistributors.length === 0) {
      return null
    }

    return (
      <DistributorBrandManufacturerFilter
        componentTypes={filterProps.componentTypes ? filterProps.componentTypes : []}
        distributors={effectiveDistributors}
        otherComponentTypes={selectorConfigTypes}
        {...config}
        {...filterProps}
      />
    )
  }

  return {
    key: DISTRIBUTOR_BRAND_FILTER_KEY_V2,
    component: FilterComponent,
  }
}

export default createDistributorBrandFilterNode
